"use client";
import React, { useState } from "react";
import dynamic from "next/dynamic";
import { stegaClean } from "next-sanity";
import { cn } from "@repo/ui/utils";
import { PlayButton } from "@repo/ui/play-button";
import { ImageWrapper } from "../image/image.component";
import type { VideoPlayerConfigData, UrlVideoSource } from "@/lib/types";

// react-player v3 renamed the URL prop from `url` (v2) to `src` —
// the v2 name is silently ignored, so we have to use `src` or the
// fallback native <video> renders with no source. See VAW-777.
const ReactPlayer = dynamic(() => import("react-player"), {
  ssr: false,
}) as React.ComponentType<{
  src: string;
  playing?: boolean;
  loop?: boolean;
  controls?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  onPlay?: () => void;
}>;

export interface VideoPlayerWrapperProps {
  source?: UrlVideoSource;
  config: VideoPlayerConfigData;
  className?: string;
  /**
   * Render the video as a decorative background — autoplays muted on a
   * loop, fills the container, hides controls and the play overlay.
   *
   * Mirrors MuxVideoPlayer so the same Sanity config drives both players.
   * Note: YouTube only autoplays when muted (browser policy), and an iframe
   * can't be cropped object-fit: cover the way a Mux <video> can.
   */
  asBackground?: boolean;
  /** Marks the thumbnail poster as the LCP candidate (above-the-fold heroes). */
  priority?: boolean;
}

export function VideoPlayerWrapper({
  source,
  config,
  className,
  asBackground = false,
  priority = false,
}: VideoPlayerWrapperProps): JSX.Element | null {
  const src = stegaClean(source?.url);
  const autoPlay = asBackground || (config?.autoPlay ?? false);
  const isMuted = autoPlay || (config?.isMuted ?? false);
  const isLooping = asBackground || (config?.isLooping ?? false);
  const showControls = asBackground ? false : (config?.showControls ?? true);
  const showPlayOverlay = !asBackground && !autoPlay;
  const aspectRatio = stegaClean(config?.visualOrientation);
  const thumbnail = config?.thumbnail;

  const [hasPlayed, setHasPlayed] = useState(autoPlay);

  const handlePlay = (): void => {
    setHasPlayed(true);
  };

  if (!src) {
    return null;
  }

  return (
    <div
      className={cn(
        "relative flex h-full w-full items-center justify-center",
        asBackground && "overflow-hidden",
        className,
      )}
      style={
        asBackground
          ? undefined
          : {
              aspectRatio:
                !aspectRatio || aspectRatio === "0" ? "16/9" : aspectRatio,
            }
      }
    >
      <ReactPlayer
        className="h-full w-full"
        controls={showControls}
        height="100%"
        loop={isLooping}
        muted={isMuted}
        playing={hasPlayed}
        playsInline={asBackground || undefined}
        src={src}
        width="100%"
        onPlay={handlePlay}
      />
      {/* Custom thumbnail poster — sits above the player (including the
          provider's own facade, e.g. YouTube) until the viewer hits play,
          per the schema's "override any default thumbnail" intent. Skipped
          in background mode, which autoplays straight through. */}
      {thumbnail && showPlayOverlay && !hasPlayed && (
        <div className="absolute inset-0 z-10">
          <ImageWrapper
            visual={thumbnail}
            className="h-full w-full"
            priority={priority}
          />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <PlayButton className="pointer-events-auto" onClick={handlePlay} />
          </div>
        </div>
      )}
    </div>
  );
}

VideoPlayerWrapper.displayName = "VideoPlayerWrapper";
